import { Divider, IconButton, List, ListItem, ListItemButton, ListItemText, Typography, Paper, Box, Button } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EditOutlined as EditOutlinedIcon, DeleteOutline as DeleteOutlineIcon, Add as AddOutlinedIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useCompanyCore } from "../../../../core/modules/company";
import { CREATE_STORE_DIALOG } from "../../../../core/constants/dialog";
import { useGlobalCore } from "../../../../core/modules/global";
import { Store } from "../../../../core/interfaces/store.interface";
import Swal from "sweetalert2";


const StoresTab = () => {
  const { t } = useTranslation();
  const { company, stores, setStore, deleteStore, getStores } = useCompanyCore();
  const { openDialog, showSnackbar } = useGlobalCore();

  const onEditing = useCallback((store: Store) => {
    setStore(store);
    openDialog(CREATE_STORE_DIALOG);
  }, [openDialog, setStore]);

  const onRemoving = useCallback((store: Store) => {
  // --- PASO 1: Validar la regla de negocio ---
  if (stores.length <= 1) {
    // Si es la última sucursal, muestra un error y detiene el proceso.
    showSnackbar({
      message: 'No se puede eliminar la última sucursal. Debe existir al menos una.',
      options: { variant: 'error' }
    });
    return;
  }

  // --- PASO 2: Pedir confirmación al usuario (si la validación pasa) ---
  Swal.fire({
    title: t('alert.sure.title'),
    text: `¿Realmente quieres eliminar la sucursal "${store.name}"? Esta acción no se puede deshacer.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: t('alert.sure.confirm.button'),
    cancelButtonText: t('alert.sure.cancel.button'),
  }).then((result: { isConfirmed: any; }) => {
    // --- PASO 3: Ejecutar la eliminación si el usuario confirma ---
    if (result.isConfirmed) {
      if (!company?.id) return; // Guardia de seguridad

      deleteStore(company.id, store.id, 
        () => { // onSuccess
          showSnackbar({ message: 'Sucursal eliminada con éxito.', options: { variant: 'success' } });
          getStores(company.id); // Recargar la lista de sucursales
        },
        (error: any) => { // onFailure
          const message = error.response?.data?.detail || 'No se pudo eliminar la sucursal.';
          showSnackbar({ message, options: { variant: 'error' } });
        }
      );
    }
  });
}, [company?.id, stores, deleteStore, getStores, showSnackbar, t]);

  const onAdding = useCallback(() => {
    setStore(null);
    openDialog(CREATE_STORE_DIALOG);
  }, [openDialog, setStore]);

  return (
    <Paper elevation={0} className="w-full">
      <Box className="p-6 flex justify-between items-center">
        <Typography variant="h6" className="font-semibold">Sucursales Activas</Typography>
        <Button variant="outlined" startIcon={<AddOutlinedIcon />} onClick={onAdding}>
          Añadir Sucursal
        </Button>
      </Box>
      <Divider />
      {stores.length > 0 ? (
        <List disablePadding>
          {stores.map((store, index) => (
            <ListItem
              key={store.id}
              divider={index < stores.length - 1}
              secondaryAction={
                <>
                  <IconButton edge="end" aria-label="editar" onClick={() => onEditing(store)}>
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="eliminar" onClick={() => onRemoving(store)} sx={{ ml: 1 }}>
                    <DeleteOutlineIcon color="error" />
                  </IconButton>
                </>
              }
            >
              <BusinessIcon color="action" className="mr-4"/>
              <ListItemText primary={store.name} />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography color="text.secondary" className="p-6 text-center">No hay sucursales definidas.</Typography>
      )}
    </Paper>
  );
};

export default StoresTab;